import React from 'react';
import styled from 'styled-components';
import Hero from '../components/Hero';
import FeatureCard from '../components/FeatureCard';
import RoomCard from '../components/RoomCard';
import Button from '../components/Button';
import { FaSwimmingPool, FaTree, FaWifi, FaUtensils } from 'react-icons/fa';
import Icon from '../components/Icon';

// Images d'exemple
import { heroImage, cuisineNiveau0_3, salonNiveau1_7, vueAerienne2, room1, room2, room3, room4, exterior1, livingRoom1, pool1 } from '../assets/images';

const Home: React.FC = () => {
  // Caractéristiques de la villa
  const features = [
    {
      icon: <Icon component={FaSwimmingPool} />,
      title: 'Piscine Privée',
      description: "Profitez d'une magnifique piscine privée entourée de nature pour vous détendre et vous rafraîchir.",
    },
    {
      icon: <Icon component={FaTree} />,
      title: 'Cadre Naturel',
      description: "Située dans un environnement naturel préservé, notre villa vous offre calme et sérénité.",
    },
    {
      icon: <Icon component={FaWifi} />,
      title: 'WiFi Haut Débit',
      description: "Restez connecté avec notre connexion WiFi haut débit disponible dans toute la propriété.",
    },
    {
      icon: <Icon component={FaUtensils} />,
      title: 'Cuisine Équipée',
      description: "Une cuisine moderne entièrement équipée pour préparer vos repas comme à la maison.",
    },
  ];

  // Chambres de la villa
  const rooms = [
    {
      image: room1,
      title: 'Chambre 1 (Niveau 1)',
      description: "Lit double (160), 2 personnes.",
      beds: 1,
      capacity: 2,
      size: 12,
      price: null,
      link: '/gallery',
    },
    {
      image: room2,
      title: 'Chambre 2 (Niveau 1)',
      description: "Lit double (140), 2 personnes.",
      beds: 1,
      capacity: 2,
      size: 12,
      price: null,
      link: '/gallery',
    },
    {
      image: room3,
      title: 'Chambre 3 (Niveau 0)',
      description: "Lit double (160), porte fenêtre donnant sur le jardin, 2 personnes.",
      beds: 2,
      capacity: 2,
      size: 13,
      price: null,
      link: '/gallery',
    },
    {
      image: room4,
      title: 'Chambre 4 (Niveau 0)',
      description: "Lit double (160) pouvant être séparé en 2 lits simples, 2 personnes.",
      beds: 2,
      capacity: 2,
      size: 14,
      price: null,
      link: '/gallery',
    },
  ];

  return (
    <HomeContainer>
      {/* Section Hero */}
      <Hero
        title="Villa Leïs Auro"
        subtitle="Une expérience unique au cœur de la Provence"
        backgroundImage={vueAerienne2}
        buttonText="Découvrir"
        scrollToId="about-section"
      />

      {/* Section À propos */}
      <Section id="about-section">
        <div className="container">
          <SectionTitle>
            <h2>La Villa Leïs Auro</h2>
          </SectionTitle>
          <AboutContent>
            <AboutText>
              <p>
                Nichée au cœur d'un environnement naturel préservé, la Villa Leïs Auro vous offre une expérience de séjour unique alliant nature, confort et tranquillité.
              </p>
              <p>
                Notre villa "Leïs Auro" est répartie sur 2 niveaux indépendants, offrant un total de 4 chambres avec piscine privée. C'est l'endroit idéal pour des vacances en famille, entre amis ou pour un événement spécial.
              </p>
              <p>
                <strong>Niveau 1 :</strong> 2 chambres, salle de bain, cuisine équipée et salon spacieux.<br />
                <strong>Niveau 0 :</strong> 2 chambres, salle d'eau avec douche, cuisine équipée ouverte sur une salle à manger.
              </p>
              <p>
                Située à seulement quelques minutes du centre ville, Villa Leïs Auro vous permet de profiter pleinement de votre séjour tout en vous offrant un havre de paix pour vous ressourcer.
              </p>

            </AboutText>
            <AboutImageContainer>
              <AboutImage src={heroImage} alt="Villa Leïs Auro" />
              <AboutImageOverlay />
            </AboutImageContainer>
          </AboutContent>
        </div>
      </Section>



      {/* Section Bienvenue à Callas */}
      <Section>
        <div className="container">
          <SectionTitle>
            <h2>Bienvenue à Callas</h2>
          </SectionTitle>
          <ContentGrid>
            <TextContent>
              <p>
                Bienvenue dans notre maison à Callas, un lieu de détente idéal avec piscine privée et vue magnifique sur la vallée et la forêt environnante.
              </p>
              <p>
                À seulement quelques minutes du centre du village pittoresque, cette maison sur deux niveaux indépendants est parfaite pour des séjours entre amis ou en famille.
              </p>
              <p>
                Chaque étage offre son propre espace avec deux chambres, une salle de bain et une cuisine, permettant à chacun de profiter de son intimité tout en partageant des moments inoubliables.
              </p>
            </TextContent>
            <ImageContainer>
              <Image src={exterior1} alt="Villa Leïs Auro" />
            </ImageContainer>
          </ContentGrid>
          <ButtonContainer>
                <Button to="/neighborhood" variant="outline">
                  En savoir plus sur le quartier et les alentours
                </Button>
              </ButtonContainer>
        </div>

      </Section>

      {/* Section Le Logement */}
      <Section light>
        <div className="container">
          <SectionTitle>
            <h2>Le Logement</h2>
          </SectionTitle>
          <ContentGrid reversed>
            <TextContent>
              <p>
                Cette maison spacieuse de 120 m², située à Callas, est parfaitement aménagée sur deux niveaux distincts pour offrir une véritable indépendance à ses occupants. Chacun des étages est agencé de manière autonome avec deux chambres, une salle de bain, un espace de vie avec une cuisine équipée. Ce double espace de vie permet aux familles ou groupes d'amis de cohabiter tout en préservant leur intimité et confort.
              </p>
              <p>
                À l'extérieur, la propriété s'ouvre sur un jardin ayant préservé son esprit sauvage offrant une vue à couper le souffle.
              </p>
              <p>
                La piscine privée est située au cœur du jardin, à quelques mètres de la maison. Pour accéder à la piscine, vous passerez par un petit portillon, garantissant ainsi tranquillité et sécurité. La piscine est également équipée d'une alarme de sécurité mobile pour une protection accrue.
              </p>
              <p>
                Plusieurs espaces extérieurs, aménagés pour les repas en plein air et le repos, permettent d'apprécier pleinement la tranquillité du cadre naturel.
              </p>
              <p>
                Idéalement située à seulement dix minutes à pied du centre du village provençal de Callas avec ses restaurants, ses commerces de proximité et son marché, cette maison combine une immersion en pleine nature avec toutes les commodités nécessaires pour un séjour confortable et apaisant.
              </p>
            </TextContent>
            <ImageContainer>
              <Image src={livingRoom1} alt="Salon de la Villa Leïs Auro" />
            </ImageContainer>
          </ContentGrid>
        </div>
      </Section>

      {/* Section Caractéristiques */}
      <Section light>
        <div className="container">
          <SectionTitle>
            <h2>Prestations</h2>
          </SectionTitle>
          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard
                key={index}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
              />
            ))}
          </FeaturesGrid>
        </div>
      </Section>

      {/* Section Espaces */}
      <Section>
        <div className="container">
          <SectionTitle>
            <h2>Les Espaces de la Villa</h2>
            <p>Villa entière à louer - 4 chambres, 8 personnes</p>
          </SectionTitle>
          
          <LevelSection>
            <LevelTitle>Niveau 1</LevelTitle>
            <RoomsGrid>
              <RoomCard
                image={room1}
                title="Chambre 1"
                description="Lit double (160), 2 personnes."
                beds={1}
                capacity={2}
                size={12}
                price={null}
                link="/gallery"
              />
              <RoomCard
                image={room2}
                title="Chambre 2"
                description="Lit double (140), 2 personnes."
                beds={1}
                capacity={2}
                size={12}
                price={null}
                link="/gallery"
              />
              <RoomCard
                image={salonNiveau1_7}
                title="Espace de vie"
                description="Canapé, table à manger, cuisine ouverte."
                beds={0}
                capacity={6}
                size={25}
                price={null}
                link="/gallery"
              />
            </RoomsGrid>
            <FacilitiesList>
              <FacilityItem>
                <strong>Salle de bain:</strong> Équipée d'une baignoire, lavabo et WC séparé.
              </FacilityItem>
              <FacilityItem>
                <strong>Espace de vie:</strong> Salon spacieux et cuisine entièrement équipée.
              </FacilityItem>
            </FacilitiesList>
          </LevelSection>
          
          <LevelSection>
            <LevelTitle>Niveau 0</LevelTitle>
            <RoomsGrid>
              <RoomCard
                image={room3}
                title="Chambre 3"
                description="Lit double (160), porte fenêtre donnant sur le jardin, 2 personnes."
                beds={1}
                capacity={2}
                size={13}
                price={null}
                link="/gallery"
              />
              <RoomCard
                image={room4}
                title="Chambre 4"
                description="Lit double (160) pouvant être séparé en 2 lits simples, 2 personnes."
                beds={2}
                capacity={2}
                size={14}
                price={null}
                link="/gallery"
              />
              <RoomCard
                image={cuisineNiveau0_3}
                title="Espace de vie"
                description="Table à manger, cuisine ouverte."
                beds={0}
                capacity={4}
                size={17}
                price={null}
                link="/gallery"
              />
            </RoomsGrid>
            <FacilitiesList>
              <FacilityItem>
                <strong>Salle d'eau:</strong> Équipée d'une douche, lavabo et WC séparé.
              </FacilityItem>
              <FacilityItem>
                <strong>Espace de vie:</strong> Cuisine équipée ouverte sur une salle à manger.
              </FacilityItem>
            </FacilitiesList>
          </LevelSection>
          
          <LevelSection>
            <LevelTitle>Extérieur</LevelTitle>
            <FacilitiesList>
              <FacilityItem>
                <strong>Piscine privée:</strong> Accessible par un portillon, équipée d'une alarme de sécurité.
              </FacilityItem>
              <FacilityItem>
                <strong>Jardin:</strong> Espace naturel préservé avec vue panoramique.
              </FacilityItem>
              <FacilityItem>
                <strong>Terrasses:</strong> Plusieurs espaces aménagés pour les repas et la détente.
              </FacilityItem>
              <FacilityItem>
                <strong>Stationnement:</strong> Places de parking privées.
              </FacilityItem>
            </FacilitiesList>
          </LevelSection>
          
          <ButtonContainer center>
            <Button to="/gallery">
              Voir la galerie complète
            </Button>
          </ButtonContainer>
        </div>
      </Section>

      {/* Section Services Personnalisés */}
      <Section>
        <div className="container">
          <SectionTitle>
            <h2>Services Personnalisés</h2>
          </SectionTitle>
          <ServicesContent>
            <ServicesIntro>
              <h3>✨ Améliorez votre séjour grâce à nos services personnalisés ! ✨</h3>
              <p>
                Nous faisons de votre confort notre priorité. Pour rendre votre expérience encore plus agréable, nous proposons une gamme de services additionnels qui simplifient votre séjour :
              </p>
            </ServicesIntro>
            <ServicesList>
              <ServiceItem>
                <ServiceIcon>🛒</ServiceIcon>
                <ServiceText>
                  <strong>Livraison de courses avant votre arrivée</strong>
                  <p>Retrouvez vos placards et frigo déjà remplis à votre arrivée. Plus besoin de passer par le supermarché !</p>
                </ServiceText>
              </ServiceItem>
              <ServiceItem>
                <ServiceIcon>⏰</ServiceIcon>
                <ServiceText>
                  <strong>Départs tardifs ou arrivées anticipées</strong>
                  <p>Ajustez vos horaires selon vos besoins pour un séjour plus flexible.</p>
                </ServiceText>
              </ServiceItem>
              <ServiceItem>
                <ServiceIcon>👶</ServiceIcon>
                <ServiceText>
                  <strong>Accessoires bébé</strong>
                  <p>Voyagez léger, nous avons ce qu'il faut pour vos petits trésors.</p>
                </ServiceText>
              </ServiceItem>
              <ServiceItem>
                <ServiceIcon>🎁</ServiceIcon>
                <ServiceText>
                  <strong>Et bien plus encore</strong>
                  <p>Organisation d'activités, services personnalisés, etc.</p>
                </ServiceText>
              </ServiceItem>
            </ServicesList>
            <ServicesNote>
              <p>💡 Ces petits plus sont là pour que vous puissiez profiter pleinement de votre séjour, sans aucun souci.</p>
            </ServicesNote>
          </ServicesContent>
        </div>
      </Section>

      {/* Section CTA */}
      <CtaSection>
        <div className="container">
          <CtaContent>
            <CtaTitle>Prêt à réserver votre séjour de rêve ?</CtaTitle>
            <CtaText>
              Vérifiez la disponibilité et réservez dès maintenant pour profiter de notre villa de luxe.
            </CtaText>
            <CtaButtons>
              <Button to="/availability" size="large">
                Vérifier les disponibilités
              </Button>
              <Button to="/contact" size="large">
                Nous contacter
              </Button>
            </CtaButtons>
          </CtaContent>
        </div>
      </CtaSection>
    </HomeContainer>
  );
};

const HomeContainer = styled.div``;

interface SectionProps {
  light?: boolean;
}

const Section = styled.section<SectionProps>`
  padding: 8rem 0;
  background-color: ${({ light }) => light ? 'var(--color-light)' : 'white'};
`;

const SectionTitle = styled.div`
  text-align: center;
  margin-bottom: 5rem;
  
  p {
    font-size: 1.6rem;
    color: var(--color-secondary);
    margin-top: 1rem;
  }
`;

const AboutContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  align-items: center;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const AboutText = styled.div`
  p {
    margin-bottom: 2rem;
    font-size: 1.6rem;
    line-height: 1.8;
  }
`;

const ButtonContainer = styled.div<{ center?: boolean }>`
  margin-top: 3rem;
  text-align: ${({ center }) => center ? 'center' : 'left'};
`;

const AboutImageContainer = styled.div`
  position: relative;
  height: 50rem;
  
  @media (max-width: 768px) {
    height: 40rem;
  }
`;

const AboutImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AboutImageOverlay = styled.div`
  position: absolute;
  top: 2rem;
  left: 2rem;
  right: -2rem;
  bottom: -2rem;
  border: 1px solid var(--color-accent);
  z-index: -1;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(25rem, 1fr));
  gap: 3rem;
`;

const RoomsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(30rem, 1fr));
  gap: 3rem;
  margin-bottom: 4rem;
`;

const CtaSection = styled.section`
  padding: 8rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(${heroImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: white;
`;

const CtaContent = styled.div`
  text-align: center;
  max-width: 80rem;
  margin: 0 auto;
`;

const CtaTitle = styled.h2`
  font-size: 3.6rem;
  margin-bottom: 2rem;
  color: white;
`;

const CtaText = styled.p`
  font-size: 1.8rem;
  margin-bottom: 3rem;
`;

const CtaButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  
  @media (max-width: 576px) {
    flex-direction: column;
    align-items: center;
  }
`;

export default Home;

// Styles supplémentaires pour les nouvelles sections
const ContentGrid = styled.div<{ reversed?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  align-items: center;
  direction: ${({ reversed }) => reversed ? 'rtl' : 'ltr'};
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const TextContent = styled.div`
  direction: ltr;
  
  p {
    margin-bottom: 2rem;
    font-size: 1.6rem;
    line-height: 1.8;
    color: var(--color-secondary);
  }
`;

const ImageContainer = styled.div`
  height: 40rem;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: var(--shadow);
  
  @media (max-width: 768px) {
    height: 30rem;
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const LevelSection = styled.div`
  margin-bottom: 5rem;
`;

const LevelTitle = styled.h3`
  font-size: 2.4rem;
  color: var(--color-primary);
  margin-bottom: 2.5rem;
  text-align: center;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -1rem;
    left: 50%;
    transform: translateX(-50%);
    width: 6rem;
    height: 2px;
    background-color: var(--color-accent);
  }
`;

const FacilitiesList = styled.ul`
  list-style: none;
  max-width: 800px;
  margin: 3rem auto 0;
  padding: 2rem;
  background-color: var(--color-light);
  border-radius: 8px;
`;

const FacilityItem = styled.li`
  position: relative;
  padding-left: 2rem;
  margin-bottom: 1.5rem;
  font-size: 1.6rem;
  line-height: 1.6;
  color: var(--color-secondary);
  
  &:before {
    content: '•';
    position: absolute;
    left: 0;
    color: var(--color-accent);
    font-weight: bold;
  }
  
  strong {
    color: var(--color-primary);
    font-weight: 600;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ServicesContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const ServicesIntro = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  
  h3 {
    font-size: 2.4rem;
    margin-bottom: 2rem;
    color: var(--color-primary);
  }
  
  p {
    font-size: 1.8rem;
    color: var(--color-secondary);
  }
`;

const ServicesList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 3rem;
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceItem = styled.div`
  display: flex;
  gap: 2rem;
  background-color: white;
  padding: 2.5rem;
  border-radius: 8px;
  box-shadow: var(--shadow);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const ServiceIcon = styled.div`
  font-size: 3rem;
  flex-shrink: 0;
`;

const ServiceText = styled.div`
  strong {
    display: block;
    font-size: 1.8rem;
    margin-bottom: 1rem;
    color: var(--color-primary);
  }
  
  p {
    font-size: 1.5rem;
    color: var(--color-secondary);
  }
`;

const ServicesNote = styled.div`
  text-align: center;
  margin-top: 4rem;
  
  p {
    font-size: 1.8rem;
    color: var(--color-accent);
    font-weight: 500;
  }
`;
